import { useState } from 'react';
import { Save } from 'lucide-react';

import { productionApi } from 'api/production';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import Banner1Form from 'shared/components/Banner1Form';

import BannerEditorActions from '../BannerEditor/BannerEditorActions';
import { useEditorMutation } from '../hooks/useEditorMutation';

import Banner1EditorSummary from './Banner1EditorSummary';
import { buildBanner1Payload, transformBanner1ToFormData } from './utils';

/**
 * Displays an editor for Banner 1 content.
 *
 * @param {string} aisleID - Parent aisle ID.
 * @param {Object} banner - Banner data.
 * @param {Function} handleDeleteNewEditor - Callback function to delete a new banner editor.
 * @param {string} initialView - Initial view, defaults to 'summary'.
 * @param {Function} refetchBanners - Function to refetch banners from the parent component.
 */
export default function Banner1Editor({
    aisleID,
    banner,
    handleDeleteNewEditor,
    initialView = 'summary',
    refetchBanners,
}) {
    const [view, setView] = useState(initialView);
    const [formData, setFormData] = useState(() => (banner.id ? transformBanner1ToFormData(banner) : banner));
    const { executeMutation, isSaving } = useEditorMutation({ entityName: 'Banner', refetch: refetchBanners });

    const toggleView = () => {
        setView((prev) => (prev === 'summary' ? 'form' : 'summary'));
    };

    const handleFormChange = (updatedData) => {
        setFormData(updatedData);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        await executeMutation({
            action: async () => {
                if (banner.id) {
                    const response = await productionApi.updateAisleBanner({
                        id: banner.id,
                        ...buildBanner1Payload(aisleID, formData),
                    });
                    if (!response.status) {
                        throw new Error('Failed to update banner');
                    }
                } else {
                    const response = await productionApi.createAisleBanner(buildBanner1Payload(aisleID, formData));
                    if (!response.status) {
                        throw new Error('Failed to create banner');
                    }
                    handleDeleteNewEditor?.();
                }
            },
        });
    };

    const handleDelete = async () => {
        if (!banner.id) {
            handleDeleteNewEditor();
            return;
        }
        await executeMutation({
            action: async () => {
                const response = await productionApi.deleteAisleBanner(banner.id);
                if (!response.status) {
                    throw new Error(response.msg || 'Failed to delete banner');
                }
            },
            loading: 'Deleting banner...',
            success: 'Banner deleted successfully',
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    {banner.bannerName ? (
                        <div className="flex items-center gap-1">
                            <h3>{banner.bannerName}</h3>
                            <span className="ml-2 text-sm text-muted-foreground">(Banner 1)</span>
                            <span className="ml-2 font-normal text-sm text-muted-foreground">id: {banner.id}</span>
                        </div>
                    ) : (
                        <h3>Banner 1</h3>
                    )}
                    <BannerEditorActions onDelete={handleDelete} toggleView={toggleView} view={view} />
                </CardTitle>
            </CardHeader>
            <CardContent>
                {view === 'summary' && <Banner1EditorSummary data={banner} formData={formData} />}
                {view === 'form' && (
                    <form className="flex flex-col gap-6" onSubmit={handleSave}>
                        <Banner1Form data={formData} disabled={isSaving} onChange={handleFormChange} />
                        <Button className="self-end w-content" disabled={isSaving}>
                            <Save />
                            Save changes
                        </Button>
                    </form>
                )}
            </CardContent>
        </Card>
    );
}
